import React, { useState, useEffect, useRef, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faInstagram, faYoutube, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import './styles/styles.css';
import Profile from './components/Profile';
import History from './components/History';
import Chat from './components/Chat/Chat';
import About from './components/About';
import Support from './components/Support';
import logo from './assets/logo_new_updated.png';

// Create a context for language
export const LanguageContext = React.createContext({
  language: 'en',
  setLanguage: () => {}
});

function App() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [language, setLanguage] = useState('en');
    const menuRef = useRef(null);

    useEffect(() => {
        // Fetch user's language preference from profile
        const fetchUserLanguage = async () => {
            try {
                const email = document.cookie.replace(/(?:(?:^|.*;\s*)user_email\s*=s*([^;]*).*$)|^.*$/, "$1");
                if (email) {
                    const response = await axios.get(`/api/profile`, { params: { email } });
                    if (response.data && response.data.language) {
                        setLanguage(response.data.language);
                    }
                }
            } catch (error) {
                console.error('Error fetching user language:', error);
            }
        };

        fetchUserLanguage();
    }, []);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        // Toggle body class for preventing scroll when menu is open
        if (!isMenuOpen) {
            document.body.classList.add('menu-open');
        } else {
            document.body.classList.remove('menu-open');
        }
    };

    const handleClickOutside = (event) => {
        if (menuRef.current && !menuRef.current.contains(event.target)) {
            setIsMenuOpen(false);
            document.body.classList.remove('menu-open');
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            // Ensure body class is removed when component unmounts
            document.body.classList.remove('menu-open');
        };
    }, []);

    // Get translation for menu items based on language
    const getMenuText = () => {
        const menuText = {
            en: {
                home: 'Home',
                profile: 'Profile',
                history: 'History',
                about: 'About',
                support: 'Support'
            },
            sr: {
                home: 'Početna',
                profile: 'Profil',
                history: 'Istorija',
                about: 'O nama',
                support: 'Podrška'
            }
        };
        
        return menuText[language] || menuText.en;
    };

    const texts = getMenuText();
    const languageFlag = language === 'sr' ? '🇷🇸' : '🇺🇸';

    return (
        <Router>
            <LanguageContext.Provider value={{ language, setLanguage }}>
                <div className="app-container">
                    <Header toggleMenu={toggleMenu} />
                    <div ref={menuRef} className={`menu-overlay ${isMenuOpen ? 'active' : ''}`}>
                        <div className="menu">
                            <div className="language-indicator">
                                <span className="language-flag">{languageFlag}</span>
                            </div>
                            <ul>
                                <li>
                                    <Link to="/" onClick={toggleMenu}>{texts.home}</Link>
                                </li>
                                <li>
                                    <Link to="/profile" onClick={toggleMenu}>{texts.profile}</Link>
                                </li>
                                <li>
                                    <Link to="/history" onClick={toggleMenu}>{texts.history}</Link>
                                </li>
                                <li>
                                    <Link to="/about" onClick={toggleMenu}>{texts.about}</Link>
                                </li>
                                <li>
                                    <Link to="/support" onClick={toggleMenu}>{texts.support}</Link>
                                </li>
                                <li className="social-icons">
                                    <a href="https://www.instagram.com/style_forecast.online" target="_blank" rel="noopener noreferrer">
                                        <FontAwesomeIcon icon={faInstagram} />
                                    </a>
                                    <a href="https://www.youtube.com/@StyleForecastWeb" target="_blank" rel="noopener noreferrer">
                                        <FontAwesomeIcon icon={faYoutube} />
                                    </a>
                                    <a href="https://www.linkedin.com/company/105470011" target="_blank" rel="noopener noreferrer">
                                        <FontAwesomeIcon icon={faLinkedin} />
                                    </a>
                                </li>
                                <li className="close-menu" onClick={toggleMenu}>
                                    <FontAwesomeIcon icon={faTimes} />
                                </li>
                            </ul>
                        </div>
                    </div>
                    <main className="main-content">
                        <Routes>
                            <Route path="/" element={<Chat />} />
                            <Route path="/profile" element={<Profile />} />
                            <Route path="/history" element={<History />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/support" element={<Support />} />
                        </Routes>
                    </main>
                </div>
            </LanguageContext.Provider>
        </Router>
    );
}

function Header({ toggleMenu }) {
    const navigate = useNavigate();

    return (
        <header>
            <div className="header-content">
                <div className="logo-container" onClick={() => navigate('/')}>
                    <img src={logo} alt="Style Forecast Logo" className="logo" />
                </div>
                <div className="menu-icon" onClick={toggleMenu}>
                    <FontAwesomeIcon icon={faBars} />
                </div>
            </div>
        </header>
    );
}

export default App;